import {useTheme} from "@emotion/react";
import {useLoaderData} from "@remix-run/react";
import {useEffect, useState} from "react";
import {Motion, spring} from "react-motion";
import {
  initialFontSize,
  maxStep,
  stepSize,
  useFontSize,
} from "../../context/fontSizeChange.jsx";
//import like from "../../assets/icons/interface/like.svg";
import CTA from "../../elements/CTA";
import IncreaseFontSize from "../../elements/svgicon/IncreaseFontSize.jsx";
import ReduceFontSize from "../../elements/svgicon/ReduceFontSize.jsx";
import ShareIcon from "../../elements/svgicon/ShareIcon.jsx";
import XIcon from "../../elements/svgicon/Xicon.jsx";
import {Text} from "../../elements/text";
import useHasHydrated from "../../hooks/useHasHydrated.js";
import {theme} from "../../styles/theme.js";
import {getGtagPageName} from "../../utils/getGtagPageName";
import * as gtag from "../../utils/gtag.client";
import {decodeHtmlEntities} from "../../utils/text.js";
import SectionWrapper from "../SectionWrapper";
import ShareSocialMediaLinks from "../ShareSocialMediaLinks";
import {
  HeaderInfoWrapper,
  IconWrapper,
  NavigationWrapper,
  RightContentWrapper,
  Span,
} from "./styles.js";

const TIME_WORD_READ = 60 / 200;

export default function Navigation({wordCount}) {
  const hasHydrated = useHasHydrated();
  const {increaseFontSize, reduceFontSize, fontSize} = useFontSize();
  const data = useLoaderData();
  const sections = data?.sections;
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const leftSideTexts = Object.values(sections?.acf?.left_side || {});
  const target = sections?.acf?.right_side?.cta?.target;
  const title = decodeHtmlEntities(sections?.acf?.right_side?.cta?.title);
  const url = sections?.acf?.right_side?.cta?.url;
  const timeArticleRead = TIME_WORD_READ * wordCount;
  leftSideTexts.push(
    `${Math.round(timeArticleRead / 60)} ${data?.translation?.minutes || "min"}`,
  );
  const filteredLeftText = leftSideTexts.filter((word) => word !== "");
  const {breakpoints} = useTheme();
  const {isMobile} = breakpoints;

  useEffect(() => {
    if (!hasHydrated) return;
    const body = document.querySelector("body");
    if (!body) return;
    const handler = (event) => {
      if (event.target.id !== "socialLinks") {
        setShowSocialLinks(false);
      }
    };
    body.addEventListener("click", handler);
    return () => {
      body.removeEventListener("click", handler);
    };
  }, [hasHydrated]);

  return (
    <SectionWrapper
      backgroundColor="grey"
      innerWrapperStyles={{
        position: "initial !important",
        paddingInline: isMobile ? 10 : 64,
      }}
    >
      <NavigationWrapper>
        <HeaderInfoWrapper>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: isMobile ? "center" : "initial",
            }}
          >
            {filteredLeftText.map((t, i) => {
              return (
                <div key={t}>
                  <Text styles={{wordBreak: "break-word"}} type="tag">
                    {t?.name || t}
                    <Span>{i !== filteredLeftText.length - 1 ? "|" : ""}</Span>
                  </Text>
                </div>
              );
            })}
          </div>

          {!isMobile && (
            <RightContentWrapper>
              {/* hide for now */}
              {/*<IconWrapper>*/}
              {/*    <Icon*/}
              {/*        src={like}*/}
              {/*        width={showSocialLinks ? 20 : 24}*/}
              {/*        height={showSocialLinks ? 20 : 24}*/}
              {/*    />*/}
              {/*</IconWrapper>*/}

              <Motion
                style={{
                  marginRightSocialIcons: spring(showSocialLinks ? 0 : -140, {
                    stiffness: 210,
                  }),
                  marginRightTextIcon: spring(showSocialLinks ? 140 : 0, {
                    stiffness: 210,
                  }),
                }}
              >
                {(style) => (
                  <>
                    {showSocialLinks ? (
                      <ShareSocialMediaLinks
                        styles={{
                          marginRight: style.marginRightSocialIcons,
                          alignSelf: "center",
                        }}
                      />
                    ) : (
                      <>
                        <IconWrapper
                          styles={{
                            marginRight: style.marginRightTextIcon,
                            cursor: fontSize === initialFontSize ? "default" : "pointer",
                          }}
                          onClick={reduceFontSize}
                        >
                          <ReduceFontSize
                            width={20}
                            height={20}
                            color={fontSize === initialFontSize ? "#737373" : "#27509B"}
                          />
                        </IconWrapper>
                        <IconWrapper
                          styles={{
                            marginRight: style.marginRightTextIcon,
                            cursor:
                              fontSize === initialFontSize + stepSize * maxStep
                                ? "default"
                                : "pointer",
                          }}
                          onClick={increaseFontSize}
                        >
                          <IncreaseFontSize
                            color={
                              fontSize === initialFontSize + stepSize * maxStep
                                ? "#737373"
                                : "#27509B"
                            }
                            width={20}
                            height={20}
                          />
                        </IconWrapper>
                      </>
                    )}
                  </>
                )}
              </Motion>

              <IconWrapper
                onClick={() => setShowSocialLinks(!showSocialLinks)}
                id="socialLinks"
              >
                {showSocialLinks ? (
                  <XIcon width={22} height={22} className="icon" />
                ) : (
                  <ShareIcon width={24} height={24} />
                )}
              </IconWrapper>
              {title && (
                <CTA
                  variant={"viaMichelin"}
                  buttonColor={theme?.colors?.brandLightBlue}
                  type={"primary"}
                  size={"small"}
                  to={url}
                  target={target}
                  onClick={() => {
                    gtag.event({
                      event: "redirection_vm",
                      action: "click",
                      page_name: getGtagPageName(window.location.pathname),
                    });
                  }}
                >
                  {title}
                </CTA>
              )}
            </RightContentWrapper>
          )}
        </HeaderInfoWrapper>
      </NavigationWrapper>
    </SectionWrapper>
  );
}
